import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Paperclip,
  Trash2
} from 'lucide-react';
import { 
  getExpense, 
  updateExpense, 
  getExpenseCategories 
} from '../../../../services/finance_services/expenseService';
import { 
  getExpenseAttachments,
  uploadExpenseAttachment, 
  deleteExpenseAttachment 
} from '../../../../services/finance_attachment_services/expenseAttachmentService';
import ReportForm from '../../../../components/ReportForm';
import Message from '../../../../components/Message';
import useApi from '../../../../hooks/useApi';

const EditExpensePage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [amount, setAmount] = useState('');
  const [dateOfExpense, setDateOfExpense] = useState('');
  const [category, setCategory] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('');
  const [vendor, setVendor] = useState('');
  const [receiptNumber, setReceiptNumber] = useState('');
  const [categories, setCategories] = useState([]);
  const [files, setFiles] = useState([]);
  const [uploadingAttachments, setUploadingAttachments] = useState(false);
  const [existingAttachments, setExistingAttachments] = useState([]);
  const [deletingAttachments, setDeletingAttachments] = useState([]);

  // Use useApi hooks
  const { loading: updateLoading, error, successMessage, callApi: updateExpenseApi, setError, setSuccessMessage } = useApi(updateExpense, {showSuccessToast: true, msg: "Expense updated successfully"});
  const { loading: fetchLoading, error: fetchError, callApi: fetchExpenseData } = useApi(getExpense, {showSuccessToast: false});
  const { callApi: fetchCategories } = useApi(getExpenseCategories, {showSuccessToast: false});

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch expense data using useApi
        const expenseData = await fetchExpenseData(id);
        if (expenseData) {
          setTitle(expenseData.title || '');
          setDescription(expenseData.description || '');
          setAmount(
            expenseData.amount !== undefined && expenseData.amount !== null
              ? String(expenseData.amount)
              : ''
          );
          setDateOfExpense(
            expenseData.date_of_expense
              ? new Date(expenseData.date_of_expense).toISOString().slice(0, 10)
              : ''
          );
          setCategory(expenseData.category ? String(expenseData.category) : '');
          setPaymentMethod(expenseData.payment_method || '');
          setVendor(expenseData.vendor || '');
          setReceiptNumber(expenseData.receipt_number || '');
        }

        // Fetch categories using useApi
        const catData = await fetchCategories();
        if (catData) {
          setCategories(catData);
        }

        // Fetch existing attachments
        const attachments = await getExpenseAttachments(id);
        setExistingAttachments(attachments?.data || []);
      } catch (err) {
        console.error('Error fetching data:', err);
      }
    };
    
    fetchData();
  }, [id, fetchExpenseData, fetchCategories]);

  const handleFileChange = (e) => {
    setFiles([...e.target.files]);
  };

  const handleDeleteAttachment = async (attachmentId) => {
    try {
      setDeletingAttachments(prev => [...prev, attachmentId]);
      await deleteExpenseAttachment(id, attachmentId);
      setExistingAttachments(prev => 
        prev.filter(attachment => attachment.id !== attachmentId)
      );
    } catch (err) {
      setError(err.message);
    } finally {
      setDeletingAttachments(prev => 
        prev.filter(id => id !== attachmentId)
      );
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const expenseData = {
      title,
      description,
      amount,
      date_of_expense: dateOfExpense,
      category,
      payment_method: paymentMethod,
      vendor,
      receipt_number: receiptNumber,
    };

    try {
      // Update the expense using useApi
      await updateExpenseApi(id, expenseData);
      
      // Handle new attachments if any
      if (files.length > 0) {
        setUploadingAttachments(true);
        await Promise.all(
          files.map(file => {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('title', file.name);
            return uploadExpenseAttachment(id, formData);
          })
        );
        setUploadingAttachments(false);
        
        // Refresh attachments after upload
        const attachments = await getExpenseAttachments(id);
        setExistingAttachments(attachments?.data || []);
      }

      setSuccessMessage('Expense updated successfully!');
      setTimeout(() => {
        navigate('/dashboard/expense-reports');
      }, 1500);
    } catch (err) {
      setError(err.message);
    }
  };

  const formFields = [
    { name: 'title', label: 'Title', type: 'text', value: title, onChange: (e) => setTitle(e.target.value), required: true },
    { name: 'description', label: 'Description', type: 'textarea', value: description, onChange: (e) => setDescription(e.target.value) },
    { name: 'amount', label: 'Amount', type: 'number', value: amount, onChange: (e) => setAmount(e.target.value), required: true },
    { name: 'date_of_expense', label: 'Date of Expense', type: 'date', value: dateOfExpense, onChange: (e) => setDateOfExpense(e.target.value), required: true },
    { name: 'category', label: 'Category', type: 'select', value: category, onChange: (e) => setCategory(e.target.value), options: categories || [], required: true },
    { name: 'payment_method', label: 'Payment Method', type: 'text', value: paymentMethod, onChange: (e) => setPaymentMethod(e.target.value) },
    { name: 'vendor', label: 'Vendor', type: 'text', value: vendor, onChange: (e) => setVendor(e.target.value) },
    { name: 'receipt_number', label: 'Receipt Number', type: 'text', value: receiptNumber, onChange: (e) => setReceiptNumber(e.target.value) },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <button
          onClick={() => navigate('/dashboard/expense-reports')}
          className="inline-flex items-center px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Expenses
        </button>
        <h1 className="text-2xl font-bold text-gray-900">Edit Expense</h1>
      </div>

      {/* Display API Error Message */}
      <Message type="error" message={error || fetchError} />

      {/* Display Success Message */}
      <Message type="success" message={successMessage} />

      {/* Expense Form */}
      <ReportForm
        title="Edit Expense"
        fields={formFields}
        onSubmit={handleSubmit}
        onCancel={() => navigate('/dashboard/expense-reports')}
        loading={updateLoading || uploadingAttachments}
        submitButtonText="Update Expense"
      >
        {/* Existing Attachments */}
        {existingAttachments.length > 0 && (
          <div>
            <label className="block text-sm font-medium text-gray-700">Current Attachments</label>
            <ul className="mt-2 space-y-2">
              {existingAttachments.map(attachment => (
                <li key={attachment.id} className="flex items-center justify-between p-2 bg-gray-50 rounded-md">
                  <div className="flex items-center">
                    <Paperclip className="h-4 w-4 mr-2 text-gray-500" />
                    <span>{attachment.title || attachment.file_name}</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleDeleteAttachment(attachment.id)}
                    className="text-red-600 hover:text-red-800"
                    disabled={deletingAttachments.includes(attachment.id)}
                  >
                    {deletingAttachments.includes(attachment.id) ? (
                      'Deleting...'
                    ) : (
                      <Trash2 className="h-4 w-4" />
                    )}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* New Attachments */}
        <div>
          <label htmlFor="attachments" className="block text-sm font-medium text-gray-700">
            Add New Attachments (Receipts, Invoices, etc.)
          </label>
          <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md">
            <div className="space-y-1 text-center">
              <Paperclip className="mx-auto h-12 w-12 text-gray-400" />
              <div className="flex text-sm text-gray-600">
                <label
                  htmlFor="file-upload"
                  className="relative cursor-pointer bg-white rounded-md font-medium text-primary-600 hover:text-primary-500 focus-within:outline-none"
                >
                  <span>Upload files</span>
                  <input
                    id="file-upload"
                    name="file-upload"
                    type="file"
                    className="sr-only"
                    multiple
                    onChange={handleFileChange}
                  />
                </label>
                <p className="pl-1">or drag and drop</p>
              </div>
              <p className="text-xs text-gray-500">
                PDF, JPG, PNG up to 10MB
              </p>
            </div>
          </div>
          {files.length > 0 && (
            <div className="mt-2">
              <p className="text-sm text-gray-500">Files to upload:</p>
              <ul className="mt-1 text-sm text-gray-700">
                {Array.from(files).map((file, index) => (
                  <li key={index} className="flex items-center">
                    <Paperclip className="h-4 w-4 mr-2 text-gray-500" />
                    {file.name} ({Math.round(file.size / 1024)} KB)
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </ReportForm>
    </div>
  );
};

export default EditExpensePage;